/**
 * Toggle: one thing that is on or off (cockatrice-ui `src/ui/Toggle.jsx`, ported to hyperscript; PLAN.md §0).
 * - variant "switch" (default): an on/off switch that applies at once. Same as Switch, same props.
 * - variant "button": a button that stays pressed (aria-pressed), for modes and formatting:
 *   Focus mode, Show notes, Bold. Takes `icon`, `label`, `pressed` / `defaultPressed`, `onChange(pressed)`.
 */
import './css/toggle.css';
import { createSignal, mergeProps, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import Switch from './Switch.ts';
import type { SwitchProps } from './Switch.ts';
import { component, el, when, whenSome } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

/** variant "switch" (default): exactly the Switch props. */
export interface ToggleSwitchVariantProps extends SwitchProps {
  /** Renders a Switch. */
  readonly variant?: 'switch' | undefined;
}

/** variant "button": a button that stays pressed. */
export interface ToggleButtonVariantProps extends Attributes {
  /** Renders a pressed/unpressed button (aria-pressed). */
  readonly variant: 'button';
  /** Visible label; the accessible name when iconOnly. Doesn't change with the state. */
  readonly label: string;
  /** Controlled pressed state. */
  readonly pressed?: boolean | undefined;
  /** Uncontrolled starting state. */
  readonly defaultPressed?: boolean | undefined;
  /** Called with the new state. */
  readonly onChange?: ((pressed: boolean) => void) | undefined;
  /** Icon before the label. */
  readonly icon?: IconName | undefined;
  /** Show only the icon; the label becomes aria-label. */
  readonly iconOnly?: boolean | undefined;
  /** 36px (sm) or 40px (md). */
  readonly size?: 'sm' | 'md' | undefined;
  /** Disabled; say why nearby. */
  readonly disabled?: boolean | undefined;
  /** Extra class. */
  readonly class?: string | undefined;
}

export type ToggleProps = ToggleSwitchVariantProps | ToggleButtonVariantProps;

export default function Toggle(props: ToggleProps): Child {
  if (props.variant !== 'button') {
    const [, rest] = splitProps(props, ['variant']);
    return component(Switch, rest);
  }
  return component(ToggleButton, props);
}

function ToggleButton(props: ToggleButtonVariantProps): Child {
  const [local, rest] = splitProps(props, ['variant', 'pressed', 'defaultPressed', 'onChange', 'icon', 'label', 'iconOnly', 'size', 'class', 'disabled']);
  const [inner, setInner] = createSignal(local.defaultPressed === true);
  const isOn = (): boolean => local.pressed ?? inner();
  const toggle = (): void => {
    if (local.disabled === true) return;
    const next = !isOn();
    if (local.pressed === undefined) setInner(next);
    local.onChange?.(next);
  };
  // The JSX spreads the caller's attributes after its own, so the caller's win here.
  return el(
    'button',
    mergeProps(
      {
        type: 'button',
        class: () => ['ui-toggle', `ui-toggle--${local.size ?? 'md'}`, local.iconOnly === true && 'ui-toggle--icon-only', local.class].filter(Boolean).join(' '),
        'aria-pressed': () => (isOn() ? 'true' : 'false'),
        'aria-label': () => (local.iconOnly === true ? local.label : undefined),
        disabled: () => local.disabled,
        onClick: toggle,
      },
      rest,
    ),
    whenSome(
      () => local.icon,
      (icon) =>
        component(Icon, {
          get name() {
            return icon();
          },
          get size() {
            return local.size === 'sm' ? 'sm' : 'md';
          },
        }),
    ),
    when(
      () => local.iconOnly !== true,
      () => el('span', {}, () => local.label),
    ),
  );
}
